import React, { useCallback, useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Plus, X, Link as LinkIcon } from "react-feather";
import { useDelete, usePost, usePut } from "../../api";
import api from "../../api/api";
import { useAuth } from "../../hooks/auth";
import {
  IAffiliateAdvertiser,
  IAdvertisersResponse,
  IAffiliateProduct,
  ICreateManualAdvertiserRequest,
  ICreateManualProductRequest,
  IUpdateManualAdvertiserRequest,
  IUpdateManualProductRequest,
  IProductUrlScrapeResult,
  IProductUrlScrapeRequest,
} from "../../interfaces";

type AdvertiserOption = {
  value: string;
  label: string;
  advertiser: IAffiliateAdvertiser;
};

interface AdvertiserFormData {
  advertiserId: string;
  name: string;
  region: string;
  websiteUrl: string;
  domain: string;
  description: string;
  imageUrl: string;
  currency: string;
}

const EMPTY_ADVERTISER: AdvertiserFormData = {
  advertiserId: "",
  name: "",
  region: "",
  websiteUrl: "",
  domain: "",
  description: "",
  imageUrl: "",
  currency: "",
};

interface ProductFormData {
  name: string;
  description: string;
  categories: string[];
  price: string;
  originalPrice: string;
  salePrice: string;
  currency: string;
  inStock: boolean;
  stockQuantity: string;
  imageUrl: string;
  productUrl: string;
  manufacturer: string;
  networkManufacturerId: string;
  sku: string;
  ean: string;
  condition: string;
  color: string;
  size: string;
}

const EMPTY_PRODUCT: ProductFormData = {
  name: "",
  description: "",
  categories: [],
  price: "",
  originalPrice: "",
  salePrice: "",
  currency: "",
  inStock: true,
  stockQuantity: "",
  imageUrl: "",
  productUrl: "",
  manufacturer: "",
  networkManufacturerId: "",
  sku: "",
  ean: "",
  condition: "",
  color: "",
  size: "",
};

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 31, fontSize: 13 }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({
    ...base,
    "> div": { padding: 4 },
  }),
};

const ValueListEditor: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}> = ({ label, items, onChange, placeholder }) => {
  const [input, setInput] = useState("");

  const addItem = () => {
    const value = input.trim();
    if (!value) {
      return;
    }

    const exists = items.some(
      (item) => item.toLowerCase() === value.toLowerCase()
    );
    if (!exists) {
      onChange([...items, value]);
    }
    setInput("");
  };

  return (
    <div>
      <Form.Label className="small mb-1">
        {label} <span className="text-muted">({items.length})</span>
      </Form.Label>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {items.length === 0 && <span className="text-muted small">None</span>}
        {items.map((item) => (
          <Badge
            key={item}
            bg="light"
            text="dark"
            className="d-flex align-items-center gap-1"
            title={item}
          >
            {item.length > 100 ? `${item.substring(0, 100)}...` : item}
            <X
              size={10}
              style={{ cursor: "pointer" }}
              onClick={() =>
                onChange(items.filter((current) => current !== item))
              }
            />
          </Badge>
        ))}
      </div>
      <InputGroup size="sm">
        <Form.Control
          placeholder={placeholder || "Add value..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <Button
          variant="outline-primary"
          onClick={addItem}
          disabled={!input.trim()}
        >
          <Plus size={12} />
        </Button>
      </InputGroup>
    </div>
  );
};

export const AffiliateAdvertiserTypeahead: React.FC<{
  network?: string;
  value: IAffiliateAdvertiser | null;
  onChange: (advertiser: IAffiliateAdvertiser | null) => void;
  placeholder?: string;
}> = ({ network, value, onChange, placeholder = "Search advertiser..." }) => {
  const { getToken } = useAuth();
  const abortRef = useRef<AbortController | null>(null);

  // One advertiser per geo shares the same name on a domain, so the region is
  // what tells two "Samsung UK" entries apart.
  const advertiserLabel = (a: IAffiliateAdvertiser) =>
    `${a.name}${a.region ? ` (${a.region})` : ""} (${a.advertiserId})`;

  const selected = value
    ? {
        value: value.id,
        label: advertiserLabel(value),
        advertiser: value,
      }
    : null;

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: AdvertiserOption[]) => void) => {
      if (!inputValue || inputValue.length < 2) {
        callback([]);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      (async () => {
        try {
          const token = await getToken();
          const params = new URLSearchParams();
          params.set("q", inputValue);
          params.set("limit", "20");
          params.set("offset", "0");
          if (network) params.set("network", network);

          const response = await api.get<IAdvertisersResponse>(
            `affiliate-products/advertisers?${params.toString()}`,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : undefined,
              },
              signal: controller.signal,
            }
          );

          callback(
            (response.advertisers || []).map((advertiser) => ({
              value: advertiser.id,
              label: advertiserLabel(advertiser),
              advertiser,
            }))
          );
        } catch {
          if (!controller.signal.aborted) callback([]);
        }
      })();
    },
    [getToken, network]
  );

  return (
    <AsyncSelect<AdvertiserOption, false>
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      value={selected}
      onChange={(option) => onChange(option?.advertiser || null)}
      placeholder={placeholder}
      isClearable
      styles={selectStyles}
      formatOptionLabel={(option) => (
        <div className="d-flex justify-content-between align-items-center gap-2">
          <span>{option.advertiser.name}</span>
          <span className="text-muted small">
            {option.advertiser.affiliateNetwork} ·{" "}
            {option.advertiser.advertiserId}
          </span>
        </div>
      )}
    />
  );
};

export const ManualAdvertiserModal: React.FC<{
  show: boolean;
  advertiser: IAffiliateAdvertiser | null;
  onHide: () => void;
  onSaved: (advertiser: IAffiliateAdvertiser) => void | Promise<void>;
}> = ({ show, advertiser, onHide, onSaved }) => {
  const isEdit = !!advertiser;
  const [form, setForm] = useState<AdvertiserFormData>(EMPTY_ADVERTISER);
  const [error, setError] = useState<string | null>(null);

  const post = usePost<IAffiliateAdvertiser, ICreateManualAdvertiserRequest>();
  const put = usePut<IAffiliateAdvertiser, IUpdateManualAdvertiserRequest>();

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (advertiser) {
      setForm({
        advertiserId: advertiser.advertiserId,
        name: advertiser.name,
        region: advertiser.region || "",
        websiteUrl: advertiser.websiteUrl || "",
        domain: advertiser.domain || "",
        description: advertiser.description || "",
        imageUrl: advertiser.imageUrl || "",
        currency: advertiser.currency || "",
      });
      return;
    }
    setForm(EMPTY_ADVERTISER);
  }, [show, advertiser]);

  const handleChange = (field: keyof AdvertiserFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      let saved: IAffiliateAdvertiser;
      if (isEdit) {
        saved = await put.execute(
          `affiliate-products/advertisers/Manual/${advertiser!.advertiserId}`,
          {
            name: form.name || undefined,
            region: form.region || undefined,
            websiteUrl: form.websiteUrl || undefined,
            domain: form.domain || undefined,
            description: form.description || undefined,
            imageUrl: form.imageUrl || undefined,
            currency: form.currency || undefined,
          }
        );
      } else {
        saved = await post.execute("affiliate-products/advertisers", {
          advertiserId: form.advertiserId || undefined,
          name: form.name,
          region: form.region || undefined,
          websiteUrl: form.websiteUrl || undefined,
          domain: form.domain || undefined,
          description: form.description || undefined,
          imageUrl: form.imageUrl || undefined,
          currency: form.currency || undefined,
        });
      }
      await onSaved(saved);
      onHide();
    } catch (err: any) {
      setError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    }
  };

  const loading = post.loading || put.loading;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Manual Advertiser" : "Add Manual Advertiser"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
          {!isEdit && (
            <Col md={6}>
              <Form.Label className="small">
                Advertiser ID <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="my-store-id"
                value={form.advertiserId}
                onChange={(e) => handleChange("advertiserId", e.target.value)}
              />
            </Col>
          )}
          <Col md={isEdit ? 12 : 6}>
            <Form.Label className="small">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              size="sm"
              placeholder="My Store"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Domain</Form.Label>
            <Form.Control
              size="sm"
              placeholder="mystore.com"
              value={form.domain}
              onChange={(e) => handleChange("domain", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Region</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GB"
              value={form.region}
              onChange={(e) => handleChange("region", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Currency</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GBP"
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Website URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://mystore.com"
              value={form.websiteUrl}
              onChange={(e) => handleChange("websiteUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Image / Logo URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://mystore.com/logo.png"
              value={form.imageUrl}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || !form.name.trim()}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Advertiser"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const ManualProductModal: React.FC<{
  show: boolean;
  advertiser: Pick<IAffiliateAdvertiser, "advertiserId" | "name" | "currency">;
  product: IAffiliateProduct | null;
  onHide: () => void;
  onSaved: (product: IAffiliateProduct) => void | Promise<void>;
}> = ({ show, advertiser, product, onHide, onSaved }) => {
  const isEdit = !!product;
  const [form, setForm] = useState<ProductFormData>(EMPTY_PRODUCT);
  const [error, setError] = useState<string | null>(null);

  const post = usePost<IAffiliateProduct, ICreateManualProductRequest>();
  const put = usePut<IAffiliateProduct, IUpdateManualProductRequest>();

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (product) {
      setForm({
        name: product.name,
        description: product.description || "",
        categories: product.categories || [],
        price: product.price != null ? String(product.price) : "",
        originalPrice:
          product.originalPrice != null ? String(product.originalPrice) : "",
        salePrice: product.salePrice != null ? String(product.salePrice) : "",
        currency: product.currency || advertiser.currency || "",
        inStock: product.inStock ?? true,
        stockQuantity:
          product.stockQuantity != null ? String(product.stockQuantity) : "",
        imageUrl: product.imageUrl || "",
        productUrl: product.productUrl || "",
        manufacturer: product.manufacturer || "",
        networkManufacturerId: product.networkManufacturerId || "",
        sku: product.sku || "",
        ean: product.ean || "",
        condition: product.condition || "",
        color: product.color || "",
        size: product.size || "",
      });
      return;
    }
    setForm({ ...EMPTY_PRODUCT, currency: advertiser.currency || "" });
  }, [show, product, advertiser.currency]);

  const handleChange = (
    field: keyof ProductFormData,
    value: string | boolean | string[]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildBody = (): ICreateManualProductRequest => {
    return {
      name: form.name,
      description: form.description || undefined,
      categories: form.categories.length ? form.categories : undefined,
      price: form.price !== "" ? Number(form.price) : undefined,
      originalPrice:
        form.originalPrice !== "" ? Number(form.originalPrice) : undefined,
      salePrice: form.salePrice !== "" ? Number(form.salePrice) : undefined,
      currency: form.currency || undefined,
      inStock: form.inStock,
      stockQuantity:
        form.stockQuantity !== "" ? Number(form.stockQuantity) : undefined,
      imageUrl: form.imageUrl || undefined,
      productUrl: form.productUrl || undefined,
      manufacturer: form.manufacturer || undefined,
      networkManufacturerId: form.networkManufacturerId || undefined,
      sku: form.sku || undefined,
      ean: form.ean || undefined,
      condition: form.condition || undefined,
      color: form.color || undefined,
      size: form.size || undefined,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      let saved: IAffiliateProduct;
      if (isEdit) {
        saved = await put.execute(
          `affiliate-products/products/${advertiser.advertiserId}/${encodeURIComponent(product!.id)}`,
          buildBody()
        );
      } else {
        saved = await post.execute(
          `affiliate-products/products/${advertiser.advertiserId}`,
          buildBody()
        );
      }
      await onSaved(saved);
      onHide();
    } catch (err: any) {
      setError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    }
  };

  const loading = post.loading || put.loading;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Manual Product" : "Add Manual Product"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="light" className="py-2 small">
          Adding under <strong>{advertiser.name}</strong>
        </Alert>
        <Row className="g-3">
          <Col md={12}>
            <Form.Label className="small">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              size="sm"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <ValueListEditor
              label="Categories"
              items={form.categories}
              onChange={(items) => handleChange("categories", items)}
              placeholder="Add category..."
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              value={form.price}
              onChange={(e) => handleChange("price", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Original Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              value={form.originalPrice}
              onChange={(e) => handleChange("originalPrice", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Sale Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              value={form.salePrice}
              onChange={(e) => handleChange("salePrice", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Currency</Form.Label>
            <Form.Control
              size="sm"
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Manufacturer</Form.Label>
            <Form.Control
              size="sm"
              value={form.manufacturer}
              onChange={(e) => handleChange("manufacturer", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Manufacturer ID</Form.Label>
            <Form.Control
              size="sm"
              value={form.networkManufacturerId}
              onChange={(e) =>
                handleChange("networkManufacturerId", e.target.value)
              }
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">SKU</Form.Label>
            <Form.Control
              size="sm"
              value={form.sku}
              onChange={(e) => handleChange("sku", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">EAN</Form.Label>
            <Form.Control
              size="sm"
              value={form.ean}
              onChange={(e) => handleChange("ean", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Condition</Form.Label>
            <Form.Select
              size="sm"
              value={form.condition}
              onChange={(e) => handleChange("condition", e.target.value)}
            >
              <option value="">—</option>
              <option value="New">New</option>
              <option value="Used">Used</option>
              <option value="Refurbished">Refurbished</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small">Color</Form.Label>
            <Form.Control
              size="sm"
              value={form.color}
              onChange={(e) => handleChange("color", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Size</Form.Label>
            <Form.Control
              size="sm"
              value={form.size}
              onChange={(e) => handleChange("size", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Stock Quantity</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              value={form.stockQuantity}
              onChange={(e) => handleChange("stockQuantity", e.target.value)}
            />
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              label="In Stock"
              checked={form.inStock}
              onChange={(e) => handleChange("inStock", e.target.checked)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Image URL</Form.Label>
            <Form.Control
              size="sm"
              value={form.imageUrl}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Product URL</Form.Label>
            <Form.Control
              size="sm"
              value={form.productUrl}
              onChange={(e) => handleChange("productUrl", e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || !form.name.trim()}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Product"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const useDeleteManualProduct = () => useDelete<void>();
export const useDeleteManualAdvertiser = () => useDelete<void>();

// ── ProductUrlModal ─────────────────────────────────────────
// Step 1 – user pastes a URL → backend scrapes + AI fills a form
// Step 2 – user reviews / edits pre-filled data → save

type ProductUrlModalStep = "input" | "reviewing" | "saving";

interface AdvertiserDraftForm {
  name: string;
  domain: string;
  websiteUrl: string;
  description: string;
  imageUrl: string;
  currency: string;
  region: string;
}

const EMPTY_ADVERTISER_DRAFT: AdvertiserDraftForm = {
  name: "",
  domain: "",
  websiteUrl: "",
  description: "",
  imageUrl: "",
  currency: "",
  region: "",
};

/**
 * ProductUrlModal
 *
 * - When `advertiser` is provided: we already know the advertiser, so the
 *   modal only asks to review/edit the pre-filled product fields and saves
 *   directly against that advertiser.
 *
 * - When `advertiser` is null: the user must also confirm / edit the scraped
 *   advertiser details. A new Manual advertiser is created first, then the
 *   product is added to it.
 */
export const ProductUrlModal: React.FC<{
  show: boolean;
  /** Pre-known advertiser. Pass null when creating from the global Products page. */
  advertiser: Pick<
    IAffiliateAdvertiser,
    "advertiserId" | "name" | "currency"
  > | null;
  onHide: () => void;
  onSaved: (product: IAffiliateProduct) => void | Promise<void>;
}> = ({ show, advertiser, onHide, onSaved }) => {
  const [step, setStep] = useState<ProductUrlModalStep>("input");
  const [url, setUrl] = useState("");
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isScraping, setIsScraping] = useState(false);

  const [productForm, setProductForm] =
    useState<ProductFormData>(EMPTY_PRODUCT);
  const [advertiserDraft, setAdvertiserDraft] = useState<AdvertiserDraftForm>(
    EMPTY_ADVERTISER_DRAFT
  );

  const { getToken } = useAuth();
  const postAdvertiser = usePost<
    IAffiliateAdvertiser,
    ICreateManualAdvertiserRequest
  >();
  const postProduct = usePost<IAffiliateProduct, ICreateManualProductRequest>();

  // Reset when modal opens/closes
  useEffect(() => {
    if (!show) return;
    setStep("input");
    setUrl("");
    setScrapeError(null);
    setSaveError(null);
    setProductForm(EMPTY_PRODUCT);
    setAdvertiserDraft(EMPTY_ADVERTISER_DRAFT);
  }, [show]);

  const handleScrape = async () => {
    if (!url.trim()) return;
    setScrapeError(null);
    setIsScraping(true);
    try {
      const token = await getToken();
      const body: IProductUrlScrapeRequest = {
        url: url.trim(),
        advertiserId: advertiser?.advertiserId,
      };
      const result = await api.post<
        IProductUrlScrapeResult,
        IProductUrlScrapeRequest
      >("affiliate-products/scrape-url", body, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      // Pre-fill product form
      const p = result.product;
      setProductForm({
        name: p.name ?? "",
        description: p.description ?? "",
        categories: p.categories ?? [],
        price: p.price != null ? String(p.price) : "",
        originalPrice: p.originalPrice != null ? String(p.originalPrice) : "",
        salePrice: p.salePrice != null ? String(p.salePrice) : "",
        currency: p.currency ?? advertiser?.currency ?? "",
        inStock: p.inStock ?? true,
        stockQuantity: "",
        imageUrl: p.imageUrl ?? "",
        productUrl: p.productUrl ?? url.trim(),
        manufacturer: p.manufacturer ?? "",
        networkManufacturerId: "",
        sku: p.sku ?? "",
        ean: p.ean ?? "",
        condition: p.condition ?? "",
        color: p.color ?? "",
        size: p.size ?? "",
      });

      // Pre-fill advertiser draft (only used when no advertiser is pre-known)
      if (!advertiser && result.advertiser) {
        const a = result.advertiser;
        setAdvertiserDraft({
          name: a.name ?? "",
          domain: a.domain ?? "",
          websiteUrl: a.websiteUrl ?? "",
          description: a.description ?? "",
          imageUrl: a.imageUrl ?? "",
          currency: a.currency ?? "",
          region: a.region ?? "",
        });
      }

      setStep("reviewing");
    } catch (err: any) {
      setScrapeError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    } finally {
      setIsScraping(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setStep("saving");
    try {
      let targetAdvertiserId = advertiser?.advertiserId ?? null;

      // Create the advertiser first if we don't have one
      if (!targetAdvertiserId) {
        const newAdvertiser = await postAdvertiser.execute(
          "affiliate-products/advertisers",
          {
            name: advertiserDraft.name,
            domain: advertiserDraft.domain || undefined,
            websiteUrl: advertiserDraft.websiteUrl || undefined,
            description: advertiserDraft.description || undefined,
            imageUrl: advertiserDraft.imageUrl || undefined,
            currency: advertiserDraft.currency || undefined,
            region: advertiserDraft.region || undefined,
          }
        );
        targetAdvertiserId = newAdvertiser.advertiserId;
      }

      const saved = await postProduct.execute(
        `affiliate-products/products/${targetAdvertiserId}`,
        {
          name: productForm.name,
          description: productForm.description || undefined,
          categories: productForm.categories.length
            ? productForm.categories
            : undefined,
          price:
            productForm.price !== "" ? Number(productForm.price) : undefined,
          originalPrice:
            productForm.originalPrice !== ""
              ? Number(productForm.originalPrice)
              : undefined,
          salePrice:
            productForm.salePrice !== ""
              ? Number(productForm.salePrice)
              : undefined,
          currency: productForm.currency || undefined,
          inStock: productForm.inStock,
          stockQuantity:
            productForm.stockQuantity !== ""
              ? Number(productForm.stockQuantity)
              : undefined,
          imageUrl: productForm.imageUrl || undefined,
          productUrl: productForm.productUrl || undefined,
          manufacturer: productForm.manufacturer || undefined,
          networkManufacturerId: productForm.networkManufacturerId || undefined,
          sku: productForm.sku || undefined,
          ean: productForm.ean || undefined,
          condition: productForm.condition || undefined,
          color: productForm.color || undefined,
          size: productForm.size || undefined,
        }
      );

      await onSaved(saved);
      onHide();
    } catch (err: any) {
      setSaveError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
      setStep("reviewing");
    }
  };

  const changeProduct = (
    field: keyof ProductFormData,
    value: string | boolean | string[]
  ) => setProductForm((prev) => ({ ...prev, [field]: value }));

  const changeAdvertiser = (field: keyof AdvertiserDraftForm, value: string) =>
    setAdvertiserDraft((prev) => ({ ...prev, [field]: value }));

  const isSaving = step === "saving";

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <LinkIcon size={16} className="me-2" />
          Import Product from URL
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── Step 1: URL input ─────────────────────── */}
        {step === "input" && (
          <>
            <p className="text-muted small mb-3">
              Paste a product page URL and we'll use AI to extract the product
              details for you to review.
            </p>
            {scrapeError && <Alert variant="danger">{scrapeError}</Alert>}
            <Form.Label className="small">
              Product URL <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                size="sm"
                placeholder="https://www.example.com/products/some-product"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleScrape();
                }}
                disabled={isScraping}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleScrape}
                disabled={isScraping || !url.trim()}
              >
                {isScraping ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Analysing…
                  </>
                ) : (
                  "Fetch & Analyse"
                )}
              </Button>
            </InputGroup>
          </>
        )}

        {/* ── Steps 2 & 3: Review form ──────────────── */}
        {(step === "reviewing" || step === "saving") && (
          <>
            {saveError && <Alert variant="danger">{saveError}</Alert>}

            {/* Advertiser section — only shown when no advertiser is pre-known */}
            {!advertiser && (
              <>
                <Alert variant="info" className="py-2 small">
                  No existing advertiser — a new <strong>Manual</strong>{" "}
                  advertiser will be created with the details below.
                </Alert>
                <h6 className="mb-2 mt-1">Advertiser</h6>
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Label className="small">
                      Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={advertiserDraft.name}
                      onChange={(e) => changeAdvertiser("name", e.target.value)}
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small">Domain</Form.Label>
                    <Form.Control
                      size="sm"
                      value={advertiserDraft.domain}
                      onChange={(e) =>
                        changeAdvertiser("domain", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Region</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="GB"
                      value={advertiserDraft.region}
                      onChange={(e) =>
                        changeAdvertiser("region", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Currency</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="GBP"
                      value={advertiserDraft.currency}
                      onChange={(e) =>
                        changeAdvertiser("currency", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Website URL</Form.Label>
                    <Form.Control
                      size="sm"
                      value={advertiserDraft.websiteUrl}
                      onChange={(e) =>
                        changeAdvertiser("websiteUrl", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={advertiserDraft.description}
                      onChange={(e) =>
                        changeAdvertiser("description", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small">Logo URL</Form.Label>
                    <Form.Control
                      size="sm"
                      value={advertiserDraft.imageUrl}
                      onChange={(e) =>
                        changeAdvertiser("imageUrl", e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </Col>
                </Row>
                <hr />
              </>
            )}

            {/* Product section */}
            {advertiser && (
              <Alert variant="light" className="py-2 small">
                Adding under <strong>{advertiser.name}</strong>
              </Alert>
            )}
            <h6 className="mb-2">Product</h6>
            <Row className="g-2">
              <Col md={12}>
                <Form.Label className="small">
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.name}
                  onChange={(e) => changeProduct("name", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={12}>
                <Form.Label className="small">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={productForm.description}
                  onChange={(e) => changeProduct("description", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={12}>
                <ValueListEditor
                  label="Categories"
                  items={productForm.categories}
                  onChange={(items) => changeProduct("categories", items)}
                  placeholder="Add category…"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Price</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step={0.01}
                  value={productForm.price}
                  onChange={(e) => changeProduct("price", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Original Price</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step={0.01}
                  value={productForm.originalPrice}
                  onChange={(e) =>
                    changeProduct("originalPrice", e.target.value)
                  }
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Sale Price</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step={0.01}
                  value={productForm.salePrice}
                  onChange={(e) => changeProduct("salePrice", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Currency</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.currency}
                  onChange={(e) => changeProduct("currency", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Manufacturer</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.manufacturer}
                  onChange={(e) =>
                    changeProduct("manufacturer", e.target.value)
                  }
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">SKU</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.sku}
                  onChange={(e) => changeProduct("sku", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">EAN</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.ean}
                  onChange={(e) => changeProduct("ean", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Condition</Form.Label>
                <Form.Select
                  size="sm"
                  value={productForm.condition}
                  onChange={(e) => changeProduct("condition", e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">—</option>
                  <option value="New">New</option>
                  <option value="Used">Used</option>
                  <option value="Refurbished">Refurbished</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Color</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.color}
                  onChange={(e) => changeProduct("color", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Size</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.size}
                  onChange={(e) => changeProduct("size", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  label="In Stock"
                  checked={productForm.inStock}
                  onChange={(e) => changeProduct("inStock", e.target.checked)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Image URL</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.imageUrl}
                  onChange={(e) => changeProduct("imageUrl", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Product URL</Form.Label>
                <Form.Control
                  size="sm"
                  value={productForm.productUrl}
                  onChange={(e) => changeProduct("productUrl", e.target.value)}
                  disabled={isSaving}
                />
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            if (step === "reviewing") {
              setStep("input");
            } else {
              onHide();
            }
          }}
          disabled={isSaving || isScraping}
        >
          {step === "reviewing" ? "Back" : "Cancel"}
        </Button>

        {step === "reviewing" && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={
              isSaving ||
              !productForm.name.trim() ||
              (!advertiser && !advertiserDraft.name.trim())
            }
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              "Save Product"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
